<template>
  <div class="batch-comparison">
    <a-page-header title="批次对比" @back="goBack">
      <template #extra>
        <a-button @click="fetchCompareData">
          <template #icon><ReloadOutlined /></template>
          刷新
        </a-button>
      </template>
    </a-page-header>

    <a-spin :spinning="loading">
      <template v-if="compareData">
        <!-- 基本信息对比 -->
        <a-card title="基本信息" style="margin-top: 16px">
          <a-table
            :columns="basicColumns"
            :data-source="basicCompareData"
            :pagination="false"
            size="small"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'diff'">
                <template v-if="record.diff === 'same'">
                  <span>-</span>
                </template>
                <template v-else-if="record.diff > 0">
                  <span style="color: #52c41a">↑ +{{ record.diff }}</span>
                </template>
                <template v-else-if="record.diff < 0">
                  <span style="color: #ff4d4f">↓ {{ record.diff }}</span>
                </template>
                <template v-else>
                  <span>-</span>
                </template>
              </template>
            </template>
          </a-table>
        </a-card>

        <!-- 阶段2对比 -->
        <a-card title="阶段2对比" style="margin-top: 16px">
          <a-row :gutter="32">
            <a-col :span="10">
              <div class="phase2-card">
                <div class="phase2-title">批次A (旧)</div>
                <div class="phase2-content">
                  <a-tag 
                    v-if="compareData.batch1?.phase2_score_level"
                    :color="getScoreLevelColor(compareData.batch1.phase2_score_level)"
                    style="font-size: 24px; padding: 8px 16px;"
                  >
                    {{ compareData.batch1.phase2_score_level }}
                  </a-tag>
                  <span v-else class="no-score">未评估</span>
                  <div class="score-value" v-if="compareData.batch1?.phase2_score_value">
                    {{ compareData.batch1.phase2_score_value }} 分
                  </div>
                </div>
              </div>
            </a-col>
            <a-col :span="4" class="arrow-col">
              <div class="arrow-container">
                <ArrowRightOutlined style="font-size: 32px" />
                <div v-if="compareData.comparison?.phase2_level_improved" class="trend-text">
                  <template v-if="compareData.comparison.phase2_level_improved === 'improved'">
                    <span style="color: #52c41a">↑ 提升</span>
                  </template>
                  <template v-else-if="compareData.comparison.phase2_level_improved === 'declined'">
                    <span style="color: #ff4d4f">↓ 下降</span>
                  </template>
                  <template v-else>
                    <span>持平</span>
                  </template>
                </div>
              </div>
            </a-col>
            <a-col :span="10">
              <div class="phase2-card">
                <div class="phase2-title">批次B (新)</div>
                <div class="phase2-content">
                  <a-tag 
                    v-if="compareData.batch2?.phase2_score_level"
                    :color="getScoreLevelColor(compareData.batch2.phase2_score_level)"
                    style="font-size: 24px; padding: 8px 16px;"
                  >
                    {{ compareData.batch2.phase2_score_level }}
                  </a-tag>
                  <span v-else class="no-score">未评估</span>
                  <div class="score-value" v-if="compareData.batch2?.phase2_score_value">
                    {{ compareData.batch2.phase2_score_value }} 分
                    <span 
                      v-if="compareData.comparison?.phase2_score_diff" 
                      :style="{ color: compareData.comparison.phase2_score_diff > 0 ? '#52c41a' : '#ff4d4f' }"
                    >
                      ({{ compareData.comparison.phase2_score_diff > 0 ? '+' : '' }}{{ compareData.comparison.phase2_score_diff }})
                    </span>
                  </div>
                </div>
              </div>
            </a-col>
          </a-row>
        </a-card>

        <!-- 阶段1对比 -->
        <a-card title="阶段1对比" style="margin-top: 16px">
          <a-table
            :columns="phase1Columns"
            :data-source="phase1CompareData"
            :pagination="false"
            size="small"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'diff'">
                <template v-if="record.diff === null || record.diff === undefined">
                  <span>-</span>
                </template>
                <template v-else-if="typeof record.diff === 'number'">
                  <span :style="{ color: record.diff > 0 ? '#52c41a' : record.diff < 0 ? '#ff4d4f' : '#999' }">
                    {{ record.diff > 0 ? '↑' : record.diff < 0 ? '↓' : '' }}
                    {{ record.diff > 0 ? '+' : '' }}{{ record.diff }}{{ record.unit || '' }}
                  </span>
                </template>
                <template v-else>
                  <span>{{ record.diff }}</span>
                </template>
              </template>
            </template>
          </a-table>
        </a-card>

        <!-- 改进分析 -->
        <a-card title="改进分析" style="margin-top: 16px">
          <template #extra>
            <a-button 
              type="primary" 
              :loading="analysisLoading"
              @click="generateAnalysis"
            >
              {{ analysisData ? '重新生成' : '生成改进分析' }}
            </a-button>
          </template>

          <template v-if="analysisData">
            <a-descriptions :column="1" bordered>
              <a-descriptions-item label="总体趋势">
                <a-tag 
                  :color="analysisData.overall_trend === 'improving' ? 'success' : 
                          analysisData.overall_trend === 'declining' ? 'error' : 'default'"
                >
                  {{ getTrendText(analysisData.overall_trend) }}
                </a-tag>
              </a-descriptions-item>
              <a-descriptions-item label="关键变化">
                <ul style="margin: 0; padding-left: 20px;">
                  <li v-for="(change, idx) in analysisData.key_changes" :key="idx">
                    {{ change }}
                  </li>
                </ul>
              </a-descriptions-item>
              <a-descriptions-item label="变化分析">
                {{ analysisData.change_analysis }}
              </a-descriptions-item>
              <a-descriptions-item label="改进建议">
                <ul style="margin: 0; padding-left: 20px;">
                  <li v-for="(suggestion, idx) in analysisData.improvement_suggestions" :key="idx">
                    {{ suggestion }}
                  </li>
                </ul>
              </a-descriptions-item>
            </a-descriptions>
          </template>
          <template v-else>
            <a-empty description="点击【生成改进分析】按钮，使用AI分析两个批次的差异和改进方向" />
          </template>
        </a-card>
      </template>

      <a-empty v-else-if="!loading" description="无对比数据" style="margin-top: 100px" />
    </a-spin>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { ReloadOutlined, ArrowRightOutlined } from '@ant-design/icons-vue';
import { useRouter, useRoute } from 'vue-router';
import { comparePlanExecutions, generateComparisonAnalysis } from '@/api/evalPlanExecutionV2';
import dayjs from 'dayjs';

const router = useRouter();
const route = useRoute();

const loading = ref(false);
const analysisLoading = ref(false);
const compareData = ref(null);
const analysisData = ref(null);

const batchId1 = computed(() => parseInt(route.query.batch1));
const batchId2 = computed(() => parseInt(route.query.batch2));

const basicColumns = [
  { title: '项目', dataIndex: 'item', key: 'item', width: 150 },
  { title: '批次A (旧)', dataIndex: 'value1', key: 'value1' },
  { title: '批次B (新)', dataIndex: 'value2', key: 'value2' },
  { title: '差异', key: 'diff', width: 100 }
];

const phase1Columns = [
  { title: '指标', dataIndex: 'item', key: 'item', width: 150 },
  { title: '批次A', dataIndex: 'value1', key: 'value1' },
  { title: '批次B', dataIndex: 'value2', key: 'value2' },
  { title: '变化', key: 'diff', width: 120 }
];

// 基本信息对比数据
const basicCompareData = computed(() => {
  if (!compareData.value) return [];
  const { batch1, batch2 } = compareData.value;
  return [
    {
      item: '批次编号',
      value1: batch1?.batch_code || '-',
      value2: batch2?.batch_code || '-',
      diff: 'same'
    },
    {
      item: '执行时间',
      value1: formatTime(batch1?.start_time),
      value2: formatTime(batch2?.start_time),
      diff: 'same'
    },
    {
      item: '执行轮次',
      value1: batch1?.test_rounds || '-',
      value2: batch2?.test_rounds || '-',
      diff: 'same'
    },
    {
      item: '总耗时',
      value1: formatDuration(batch1?.duration),
      value2: formatDuration(batch2?.duration),
      diff: compareData.value.comparison?.duration_diff
    }
  ];
});

// 阶段1对比数据
const phase1CompareData = computed(() => {
  if (!compareData.value) return [];
  const { batch1, batch2, comparison } = compareData.value;
  
  const dist1 = batch1?.phase1_score_distribution || {};
  const dist2 = batch2?.phase1_score_distribution || {};
  
  return [
    {
      item: '平均分',
      value1: batch1?.phase1_avg_score ?? '-',
      value2: batch2?.phase1_avg_score ?? '-',
      diff: comparison?.phase1_avg_diff,
      unit: ''
    },
    {
      item: 'A级占比',
      value1: formatPercent(dist1.A, batch1?.total_executions),
      value2: formatPercent(dist2.A, batch2?.total_executions),
      diff: calcPercentDiff(dist1.A, batch1?.total_executions, dist2.A, batch2?.total_executions),
      unit: '%'
    },
    {
      item: 'B级占比',
      value1: formatPercent(dist1.B, batch1?.total_executions),
      value2: formatPercent(dist2.B, batch2?.total_executions),
      diff: calcPercentDiff(dist1.B, batch1?.total_executions, dist2.B, batch2?.total_executions),
      unit: '%'
    },
    {
      item: 'C级占比',
      value1: formatPercent(dist1.C, batch1?.total_executions),
      value2: formatPercent(dist2.C, batch2?.total_executions),
      diff: calcPercentDiff(dist1.C, batch1?.total_executions, dist2.C, batch2?.total_executions),
      unit: '%'
    },
    {
      item: '通过率',
      value1: batch1?.phase1_pass_rate ? `${batch1.phase1_pass_rate}%` : '-',
      value2: batch2?.phase1_pass_rate ? `${batch2.phase1_pass_rate}%` : '-',
      diff: comparison?.pass_rate_diff,
      unit: '%'
    }
  ];
});

// 获取对比数据
const fetchCompareData = async () => {
  if (!batchId1.value || !batchId2.value) {
    message.error('缺少批次ID参数');
    return;
  }

  loading.value = true;
  try {
    const res = await comparePlanExecutions(batchId1.value, batchId2.value);
    if (res.code === 200) {
      compareData.value = res.data;
    } else {
      message.error(res.message || '获取对比数据失败');
    }
  } catch (error) {
    message.error('获取对比数据失败：' + error.message);
  } finally {
    loading.value = false;
  }
};

// 生成改进分析
const generateAnalysis = async () => {
  analysisLoading.value = true;
  try {
    const res = await generateComparisonAnalysis(batchId1.value, batchId2.value);
    if (res.code === 200) {
      analysisData.value = res.data?.analysis;
      message.success('改进分析生成成功');
    } else {
      message.error(res.message || '生成改进分析失败');
    }
  } catch (error) {
    message.error('生成改进分析失败：' + error.message);
  } finally {
    analysisLoading.value = false;
  }
};

// 返回
const goBack = () => {
  router.push('/eval-v2/results');
};

// 辅助函数
const getScoreLevelColor = (level) => {
  const colors = { A: 'success', B: 'processing', C: 'warning' };
  return colors[level] || 'default';
};

const getTrendText = (trend) => {
  const texts = {
    improving: '持续改进',
    declining: '有所下降',
    stable: '保持稳定'
  };
  return texts[trend] || trend || '未知';
};

const formatTime = (time) => {
  if (!time) return '-';
  return dayjs(time).format('MM-DD HH:mm');
};

const formatDuration = (ms) => {
  if (!ms) return '-';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainSeconds = seconds % 60;
  if (minutes > 0) {
    return `${minutes}分${remainSeconds}秒`;
  }
  return `${remainSeconds}秒`;
};

const formatPercent = (count, total) => {
  if (!count || !total) return '-';
  return `${((count / total) * 100).toFixed(0)}% (${count}/${total})`;
};

const calcPercentDiff = (count1, total1, count2, total2) => {
  if (!count1 || !total1 || !count2 || !total2) return null;
  const p1 = (count1 / total1) * 100;
  const p2 = (count2 / total2) * 100;
  return Math.round(p2 - p1);
};

onMounted(() => {
  fetchCompareData();
});
</script>

<style scoped>
.batch-comparison {
  padding: 24px;
}

.phase2-card {
  text-align: center;
  padding: 24px;
  background: #fafafa;
  border-radius: 8px;
}

.phase2-title {
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
}

.phase2-content {
  min-height: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.no-score {
  font-size: 18px;
  color: #999;
}

.score-value {
  margin-top: 12px;
  font-size: 16px;
  color: #333;
}

.arrow-col {
  display: flex;
  align-items: center;
  justify-content: center;
}

.arrow-container {
  text-align: center;
}

.trend-text {
  margin-top: 8px;
  font-size: 14px;
}
</style>

